import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharepointConfigurationEntity } from '../../domain/entities/sharepoint-configuration.entity';
import { SharepointConfigurationsApi } from '../api/sharepoint-configurations.api';
import { SharepointConfigurationsMapper } from '../mappers/sharepoint-configurations.mapper';
import { CreateSharepointConfigurationRequestDto } from '../dtos/create-sharepoint-configuration-request.dto';
import { UpdateSharepointConfigurationRequestDto } from '../dtos/update-sharepoint-configuration-request.dto';

/**
 * SharePoint Configuration Repository
 * Handles all SharePoint configuration data operations: HTTP API calls and DTO mapping.
 * Note: This is a singleton resource per tenant (one config per tenant).
 */
@Injectable({ providedIn: 'root' })
export class SharepointConfigurationsRepository {
  private readonly _api = inject(SharepointConfigurationsApi);
  private readonly _mapper = inject(SharepointConfigurationsMapper);

  /**
   * Retrieves SharePoint configuration for the current tenant.
   * @returns Observable with SharePoint configuration entity
   */
  public getSharepointConfiguration(): Observable<SharepointConfigurationEntity> {
    return this._api.getSharepointConfiguration().pipe(map((dto) => this._mapper.toDomain(dto)));
  }

  /**
   * Creates SharePoint configuration for the current tenant.
   * @param data - SharePoint configuration creation data
   * @returns Observable with the created configuration ID
   */
  public createSharepointConfiguration(data: CreateSharepointConfigurationRequestDto): Observable<{ id: number }> {
    return this._api.createSharepointConfiguration(data);
  }

  /**
   * Updates SharePoint configuration for the current tenant.
   * @param data - SharePoint configuration update data
   * @returns Observable with the updated configuration ID
   */
  public updateSharepointConfiguration(data: UpdateSharepointConfigurationRequestDto): Observable<{ id: number }> {
    return this._api.updateSharepointConfiguration(data);
  }
}
